// Core data types for the silky2 pipeline.

export type StepType =
  | 'input'
  | 'greyscale'
  | 'color_separate'
  | 'channel'
  | 'gradient_sep'
  | 'band'
  | 'halftone';

export type Units = 'in' | 'cm' | 'mm';
export type RegMarkStyle = 'none' | 'cross';
export type CornerKey = 'TL' | 'TR' | 'BL' | 'BR';
export type MirrorMode = 'none' | 'horizontal';
export type InkMode = 'opaque' | 'transparent';
export type HalftonePattern = 'dot';
export type Rotation = 0 | 90 | 180 | 270;
export type TileMode = 'auto' | 'never';

export interface PreviewBlock {
  color: string;
  ink_mode: InkMode;
  opacity: number;
  enabled: boolean;
}

export interface InputParams {
  rotation: Rotation;
}

export interface GreyscaleParams {
  r_weight: number;
  g_weight: number;
  b_weight: number;
}

export const DEFAULT_GREYSCALE_PARAMS: GreyscaleParams = {
  r_weight: 0.299,
  g_weight: 0.587,
  b_weight: 0.114,
};

export interface ColorChannel {
  name: string;
  target: string;
  threshold: number;
}

export interface ColorSeparateParams {
  channels: ColorChannel[];
}

export interface ChannelParams {
  channel_index: number;
}

export interface GradientBand {
  name: string;
  min: number;
  max: number;
}

export interface GradientSepParams {
  bands: GradientBand[];
}

export interface BandParams {
  band_index: number;
}

export interface HalftoneParams {
  pattern: HalftonePattern;
  lpi: number;
  angle: number;
  min_dot_pct: number;
  max_dot_pct: number;
  gamma: number;
}

export type StepParams =
  | InputParams
  | GreyscaleParams
  | ColorSeparateParams
  | ChannelParams
  | GradientSepParams
  | BandParams
  | HalftoneParams;

export interface Step {
  id: string;
  type: StepType;
  name: string;
  params: StepParams;
  preview?: PreviewBlock;
  children: string[];
}

export interface Pipeline {
  root_id: string;
  steps: Record<string, Step>;
}

export interface ProjectSettings {
  target_width: number;
  target_height: number;
  units: Units;
  dpi: number;
  mesh_count: number;
  reg_marks: RegMarkStyle;
  reg_marks_auto: boolean;
  mark_size_in: number;
  mark_margin_in: number;
  mark_corners: CornerKey[];
  mark_centerlines: boolean;
  mirror_output: MirrorMode;
  substrate_color: string;
  paper_width_in: number;
  paper_height_in: number;
  tile_overlap_in: number;
  tile_mode: TileMode;
}

export interface InputAsset {
  filename: string;
  mime: string;
  source_width: number;
  source_height: number;
  source_dpi: number;
}

export interface RecipePayload {
  format_version: string;
  project_settings: ProjectSettings;
  pipeline: Pipeline;
  layer_order: string[];
}

export const DEFAULT_PROJECT_SETTINGS: ProjectSettings = {
  target_width: 8.5,
  target_height: 11,
  units: 'in',
  dpi: 300,
  mesh_count: 156,
  reg_marks: 'none',
  reg_marks_auto: true,
  mark_size_in: 0.4,
  mark_margin_in: 0.25,
  mark_corners: ['TL', 'TR', 'BL', 'BR'],
  mark_centerlines: false,
  mirror_output: 'none',
  substrate_color: '#FFFFFF',
  paper_width_in: 8.5,
  paper_height_in: 11,
  tile_overlap_in: 0.5,
  tile_mode: 'never',
};

export const DEFAULT_HALFTONE_PARAMS: HalftoneParams = {
  pattern: 'dot',
  lpi: 45,
  angle: 45,
  min_dot_pct: 5,
  max_dot_pct: 95,
  gamma: 1.0,
};

export const DEFAULT_PREVIEW: PreviewBlock = {
  color: '#000000',
  ink_mode: 'opaque',
  opacity: 100,
  enabled: true,
};

export const CURRENT_FORMAT_VERSION = '1.0';
